/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

// TODO: assign to correct module
import { supabase } from "./supabaseClient.js";

// =====================================================================
// ticketService  ::  support tickets over RLS (0015–0016)
//
// Submitter sees/handles their own tickets; platform admins (Brand Command
// staff) see + manage all. Reply author identity/role is forced by a DB
// trigger — never trusted from the client.
// =====================================================================

export const TICKET_CATEGORIES = ["Question", "Bug", "Billing", "Feature request", "Other"];
export const TICKET_STATUSES = ["open", "working", "deferred", "resolved", "closed"];

// The ticket "Area" options are derived live from the registered modules — see
// shared/utils/moduleCatalog.js (getTicketAreaGroups). Re-exported here so the
// ticket UI has a single import for everything ticket-related.
export { getTicketAreaGroups } from "./moduleCatalog.js";

// Fire-and-forget email notification (no-ops if email isn't configured).
export async function notifyTicket(ticketId, event) {
  try {
    await supabase.functions.invoke("ticket-notify", { body: { ticketId, event } });
  } catch (e) {
    console.warn("[ticketService.notifyTicket]", e);
  }
}

export const ATTACH_ACCEPT = [
  "image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf",
  "text/plain", "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
export const ATTACH_MAX_MB = 10;

export function validateAttachment(file) {
  if (!ATTACH_ACCEPT.includes(file.type)) return "Unsupported file type.";
  if (file.size > ATTACH_MAX_MB * 1024 * 1024) return `Each file must be under ${ATTACH_MAX_MB} MB.`;
  return null;
}

function attachId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

// Upload one file to support-attachments/<ticketId>/<id>.<ext>. Storage RLS
// only lets a ticket's submitter/staff write under its folder.
export async function uploadTicketAttachment(ticketId, file) {
  const bad = validateAttachment(file);
  if (bad) throw new Error(bad);
  const ext = (file.name.includes(".") ? file.name.split(".").pop() : "bin")
    .toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 8) || "bin";
  const path = `${ticketId}/${attachId()}.${ext}`;
  const { error } = await supabase.storage
    .from("support-attachments")
    .upload(path, file, { contentType: file.type });
  if (error) {
    console.error("[ticketService.uploadTicketAttachment]", error);
    throw new Error("Could not upload attachment.");
  }
  return { path, name: file.name.slice(0, 200), type: file.type, size: file.size };
}

export async function attachmentSignedUrl(path) {
  const { data, error } = await supabase.storage
    .from("support-attachments")
    .createSignedUrl(path, 3600);
  if (error) {
    console.error("[ticketService.attachmentSignedUrl]", error);
    return null;
  }
  return data?.signedUrl ?? null;
}

async function uploadAll(ticketId, files) {
  const out = [];
  for (const f of files) out.push(await uploadTicketAttachment(ticketId, f));
  return out;
}

export async function isPlatformAdmin() {
  const { data, error } = await supabase.rpc("is_platform_admin");
  if (error) {
    console.error("[ticketService.isPlatformAdmin]", error);
    return false;
  }
  return data === true;
}

export async function listMyTickets() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");
  const { data, error } = await supabase
    .from("support_tickets")
    .select("id, subject, status, category, created_at, last_reply_at, last_reply_role")
    .eq("user_id", user.id) // own only, even for admins (admins use the inbox)
    .order("created_at", { ascending: false });
  if (error) {
    console.error("[ticketService.listMyTickets]", error);
    throw new Error("Could not load your tickets.");
  }
  return data ?? [];
}

export async function createTicket({ subject, body, category, module, files = [] }) {
  const s = String(subject || "").trim();
  const b = String(body || "").trim();
  if (s.length < 1 || b.length < 1) throw new Error("Please fill in a subject and a description.");

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in.");

  const { data, error } = await supabase
    .from("support_tickets")
    .insert({ user_id: user.id, subject: s.slice(0, 200), body: b.slice(0, 5000), category: category || null, module: module || null })
    .select("id")
    .single();
  if (error) {
    console.error("[ticketService.createTicket]", error);
    throw new Error("Could not submit your ticket.");
  }
  if (files.length > 0) {
    const atts = await uploadAll(data.id, files);
    const { error: aErr } = await supabase.rpc("set_ticket_attachments", { tid: data.id, atts });
    if (aErr) console.error("[ticketService.createTicket.attachments]", aErr);
  }
  notifyTicket(data.id, "new");
  return data.id;
}

export async function getTicket(id) {
  const { data, error } = await supabase
    .from("support_tickets")
    .select("id, ticket_number, subject, body, status, category, module, created_at, user_id, account_id, attachments")
    .eq("id", id)
    .single();
  if (error) {
    console.error("[ticketService.getTicket]", error);
    throw new Error("Could not load the ticket.");
  }
  return data;
}

export async function getTicketMessages(id) {
  const { data, error } = await supabase.rpc("ticket_messages", { tid: id });
  if (error) {
    console.error("[ticketService.getTicketMessages]", error);
    throw new Error("Could not load the conversation.");
  }
  return data ?? [];
}

export async function postReply(id, body, files = []) {
  const b = String(body || "").trim();
  if (b.length < 1 && files.length === 0) throw new Error("Reply can't be empty.");
  const atts = files.length > 0 ? await uploadAll(id, files) : [];
  // author_id + author_role are set by the DB trigger.
  const { error } = await supabase
    .from("support_ticket_messages")
    .insert({ ticket_id: id, body: (b || "(attachment)").slice(0, 5000), attachments: atts });
  if (error) {
    console.error("[ticketService.postReply]", error);
    throw new Error("Could not send your reply.");
  }
  notifyTicket(id, "reply");
}

const TICKET_EDIT_FIELDS = new Set(["subject", "category", "module"]);

// Staff-only (RLS) edit of a ticket's fields.
export async function updateTicketFields(id, patch) {
  const out = {};
  for (const k of TICKET_EDIT_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, k)) out[k] = patch[k];
  }
  if (out.subject != null) {
    const s = String(out.subject).trim();
    if (s.length < 1 || s.length > 200) throw new Error("Subject must be 1–200 characters.");
    out.subject = s;
  }
  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from("support_tickets").update(out).eq("id", id);
  if (error) {
    console.error("[ticketService.updateTicketFields]", error);
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only Brand Command staff can edit tickets.");
    }
    throw new Error("Could not update the ticket.");
  }
}

// Staff-only (RLS) delete; messages cascade.
export async function deleteTicket(id) {
  const { error } = await supabase.from("support_tickets").delete().eq("id", id);
  if (error) {
    console.error("[ticketService.deleteTicket]", error);
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only Brand Command staff can delete tickets.");
    }
    throw new Error("Could not delete the ticket.");
  }
}

export async function setTicketStatus(id, status) {
  if (!TICKET_STATUSES.includes(status)) throw new Error("Invalid status.");
  const { error } = await supabase.from("support_tickets").update({ status }).eq("id", id);
  if (error) {
    console.error("[ticketService.setTicketStatus]", error);
    if (error.code === "42501" || /row-level security/i.test(error.message ?? "")) {
      throw new Error("Only Brand Command staff can change a ticket's status.");
    }
    throw new Error("Could not update the ticket.");
  }
}

// Platform-admin inbox: all tickets across all tenants (returns [] for others).
export async function adminListTickets() {
  const { data, error } = await supabase.rpc("admin_list_tickets");
  if (error) {
    console.error("[ticketService.adminListTickets]", error);
    throw new Error("Could not load tickets.");
  }
  return data ?? [];
}
